export type SweepstakesLifecycleStatus = 'scheduled' | 'drawn' | 'closed';

export interface SweepstakesLifecycleState {
  status: SweepstakesLifecycleStatus;
  label: string;
}

export interface SweepstakesLifecycleInput {
  drawnAt?: string | null;
  isWinnerConfirmed?: boolean | null;
  drawingDateTime?: string | null;
  drawingDateTimeTimezone?: string | null;
  isPublished?: boolean;
}

export function getSweepstakesLifecycleState(
  input: SweepstakesLifecycleInput,
  formatDrawingDateTime?: (date: string, timezone?: string | null) => string,
): SweepstakesLifecycleState {
  const {
    drawnAt, isWinnerConfirmed, drawingDateTime, drawingDateTimeTimezone, isPublished = true,
  } = input;

  if (isWinnerConfirmed) {
    return { status: 'closed', label: 'Sweepstakes closed' };
  }

  if (drawnAt) {
    return { status: 'drawn', label: 'Drawn, awaiting confirmation' };
  }

  if (drawingDateTime) {
    const formatted = formatDrawingDateTime
      ? formatDrawingDateTime(drawingDateTime, drawingDateTimeTimezone)
      : new Date(drawingDateTime).toLocaleString();
    const suffix = isPublished ? '' : ' (only once published)';
    return { status: 'scheduled', label: `Draw scheduled for ${formatted}${suffix}` };
  }

  return { status: 'scheduled', label: isPublished ? 'Draw scheduled' : 'Draws once published' };
}

function extractErrorMessage(error: unknown): string {
  if (!error) return '';
  if (typeof error === 'string') return error;
  if (error instanceof Error) return error.message;
  const err = error as { errors?: { message?: string }[]; message?: string };
  if (err.errors?.[0]?.message) return err.errors[0].message;
  if (err.message) return err.message;
  return '';
}

export function parseSweepstakesActionError(error: unknown): string {
  const message = extractErrorMessage(error);

  if (/only the sweepstakes owner/i.test(message)) {
    return 'Only the sweepstakes owner can manage its winners.';
  }
  if (/already been confirmed and closed/i.test(message)) {
    return 'This sweepstakes is already closed. Winners can no longer be replaced.';
  }
  if (/none of the supplied ids match/i.test(message)) {
    return 'That winner could not be replaced. They may have already been replaced.';
  }
  if (/draw the winners before confirming/i.test(message)) {
    return 'Draw the winners before closing the sweepstakes.';
  }
  if (/not a sweepstakes/i.test(message)) {
    return 'This sticker is not a sweepstakes.';
  }

  return message || 'Something went wrong. Please try again.';
}

export function formatSweepstakesDate(value: string | null | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}
